import { useState } from 'react'
import { SlidersHorizontal, X } from 'lucide-react'
import type { DateRange } from 'react-day-picker'
import { cn } from '@/lib/utils'
import { PeriodPills } from '@/components/shared/period-pills'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DatePickerWithRange } from '@/components/ui/date-range-picker'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { ChonkyButton } from '@/components/shared/chonky-button'

const PERIODS = [
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: 'all', label: 'Todo período' },
] as const

export type PeriodValue = (typeof PERIODS)[number]['value']
export const DEFAULT_PERIOD: PeriodValue = '30d'

interface AnalyticsFiltersProps {
  activePeriod: PeriodValue | undefined
  selectedCampaign: string
  dateRange: DateRange | undefined
  excludeRepurchases: boolean
  hasActiveFilters: boolean
  campaigns: { id: string; name: string }[] | undefined
  onPeriodChange: (period: PeriodValue) => void
  onCampaignChange: (val: string) => void
  onDateChange: (range: DateRange | undefined) => void
  onToggleRepurchases: () => void
  onClearFilters: () => void
}

export function AnalyticsFilters({
  activePeriod,
  selectedCampaign,
  dateRange,
  excludeRepurchases,
  hasActiveFilters,
  campaigns,
  onPeriodChange,
  onCampaignChange,
  onDateChange,
  onToggleRepurchases,
  onClearFilters,
}: AnalyticsFiltersProps) {
  const [sheetOpen, setSheetOpen] = useState(false)

  const activeFilterCount = [
    selectedCampaign !== 'all',
    dateRange !== undefined,
    excludeRepurchases,
  ].filter(Boolean).length

  return (
    <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-1.5">
      {/* Period selector — always visible, scrollable on mobile */}
      <PeriodPills options={PERIODS} value={activePeriod} onChange={onPeriodChange} />

      {/* Desktop-only filters */}
      <div className="hidden sm:flex items-center gap-1.5">
        <Select value={selectedCampaign} onValueChange={onCampaignChange}>
          <SelectTrigger size="sm" className="w-40 shadow-recessed text-[13px]">
            <SelectValue placeholder="Todas as Campanhas" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as Campanhas</SelectItem>
            {campaigns?.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <DatePickerWithRange
          date={dateRange}
          setDate={onDateChange}
          align="end"
          className="[&_button#date]:sm:w-auto [&_button#date]:h-8 [&_button#date]:shadow-recessed [&_button#date]:text-[13px]"
        />

        <button
          type="button"
          onClick={onToggleRepurchases}
          className={cn(
            'inline-flex items-center h-8 px-3 text-xs font-medium rounded-md',
            'transition-all duration-150 ease-in-out whitespace-nowrap border',
            'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/12',
            excludeRepurchases
              ? 'bg-primary text-primary-foreground border-primary shadow-[0_1px_2px_rgba(16,185,129,0.3)]'
              : 'text-muted-foreground border-border hover:text-foreground motion-safe:hover:-translate-y-px hover:shadow-[0_2px_4px_rgba(0,0,0,0.08)]'
          )}
        >
          {excludeRepurchases ? 'Só 1ª compra' : 'Inclui recompras'}
        </button>
      </div>

      {/* Mobile filter trigger — opens bottom Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetTrigger asChild>
          <Button variant="outline" size="sm" className="sm:hidden shadow-recessed">
            <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />
            Filtros
            {activeFilterCount > 0 && (
              <Badge variant="default" className="ml-1.5 h-4 min-w-4 px-1 text-[10px]">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </SheetTrigger>
        <SheetContent side="bottom" className="rounded-t-xl">
          <SheetHeader>
            <SheetTitle>Filtros</SheetTitle>
          </SheetHeader>
          <SheetBody>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[13px] font-medium">Campanha</label>
                <Select value={selectedCampaign} onValueChange={onCampaignChange}>
                  <SelectTrigger className="shadow-recessed">
                    <SelectValue placeholder="Todas as Campanhas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as Campanhas</SelectItem>
                    {campaigns?.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[13px] font-medium">Período personalizado</label>
                <DatePickerWithRange date={dateRange} setDate={onDateChange} />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[13px] font-medium">Recompras</label>
                <button
                  type="button"
                  onClick={onToggleRepurchases}
                  className={cn(
                    'w-full px-3 py-2.5 min-h-[44px] text-xs font-medium rounded-md text-left',
                    'transition-all duration-150 ease-in-out border',
                    'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/12',
                    excludeRepurchases
                      ? 'bg-primary text-primary-foreground border-primary shadow-[0_1px_2px_rgba(16,185,129,0.3)]'
                      : 'text-muted-foreground border-border'
                  )}
                >
                  {excludeRepurchases ? 'Só 1ª compra' : 'Inclui recompras'}
                </button>
              </div>
            </div>
          </SheetBody>
          <SheetFooter className="flex-row">
            {hasActiveFilters && (
              <Button
                variant="ghost"
                onClick={() => {
                  onClearFilters()
                  setSheetOpen(false)
                }}
                className="flex-1"
              >
                Limpar filtros
              </Button>
            )}
            <ChonkyButton className="flex-1" onClick={() => setSheetOpen(false)}>
              Aplicar
            </ChonkyButton>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Clear filters — always last */}
      {hasActiveFilters && (
        <button
          type="button"
          onClick={onClearFilters}
          className="hidden sm:inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors duration-150"
        >
          <X className="h-3 w-3" />
          Limpar filtros
        </button>
      )}
    </div>
  )
}
